<template>
  <div>
    <default>
      <div class="flex items-center justify-between mb-4">
        <h1 class="text-2xl font-bold">Adresy</h1>
        <div class="flex space-x-3">
          <flowbite-modal btn-name="přidat" name="přidat adresu">
            <template #modal-content>
              <create-address-form />
            </template>
          </flowbite-modal>
          <button @click="fetchAddresses" :disabled="isLoading"
                  class="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-300">
            <svg v-if="isLoading" class="animate-spin h-5 w-5 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none"
                 viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v2a6 6 0 100 12v2a8 8 0 01-8-8z"></path>
            </svg>
            <span>{{ isLoading ? 'Načítám...' : 'Obnovit' }}</span>
          </button>

          <div class="">
            <div class="relative">
              <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg class="w-4 h-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                  <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                </svg>
              </div>
              <input
                  v-model="search"
                  @input="debouncedFetch"
                  type="text"
                  class="block w-full p-2 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Hledat adresu, firmu, město..."
              >
            </div>
          </div>

        </div>
      </div>


      <div v-if="isLoading && !addresses.length" class="flex justify-center items-center my-4">
        <div
            class="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12 border-t-blue-500"></div>
      </div>

      <div v-else :class="{ 'opacity-50': isLoading }" class="relative overflow-x-auto shadow-md sm:rounded-lg">
        <table class="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
          <thead class="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
          <tr>
            <th scope="col" class="px-6 py-3">Firma / Jméno</th>
            <th scope="col" class="px-6 py-3">Ulice</th>
            <th scope="col" class="px-6 py-3">Město</th>
            <th scope="col" class="px-6 py-3">PSČ</th>
            <th scope="col" class="px-6 py-3">IČO / DIČ</th>
            <th scope="col" class="px-6 py-3">Doplňující údaje</th>
            <th scope="col" class="px-6 py-3">Vytvořeno</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="address in addresses" :key="address.id"
              @click="$router.push({ name: 'address-detail', params: {addressId: address.id}})"
              class="cursor-pointer bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
            <th scope="row" class="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">
              {{ address.company || 'Osobní adresa' }}
            </th>
            <td class="px-6 py-4">{{ address.street }}</td>
            <td class="px-6 py-4">{{ address.city }}</td>
            <td class="px-6 py-4">{{ address.zip }}</td>
            <td class="px-6 py-4">
              <span v-if="address.ico">IČ: {{ address.ico }}</span>
              <span v-if="address.dic" class="block text-xs text-gray-400">DIČ: {{ address.dic }}</span>
            </td>
            <td class="px-6 py-4">
              <div v-if="address.custom && Object.keys(address.custom).length" class="space-y-0.5">
                <div v-for="(value, key) in address.custom" :key="key"
                     v-show="value !== null && value !== undefined && value !== ''"
                     class="text-xs">
                  <span class="font-medium text-gray-600 dark:text-gray-300">{{ key }}:</span>
                  <span class="ml-1">{{ value }}</span>
                </div>
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-xs">
              <span v-if="address.createdAt">{{ new Date(address.createdAt).toLocaleString('cs-CZ') }}</span>
            </td>
          </tr>
          </tbody>
        </table>

        <nav class="flex items-center flex-column flex-wrap md:flex-row justify-between py-4 px-6"
             aria-label="Table navigation">
          <div>
            <label for="limit" class="mr-2 text-sm text-gray-700">Počet na stránku:</label>
            <select v-model="limit" @change="fetchAddresses" id="limit"
                    class="text-sm text-gray-700 bg-white border border-gray-300 rounded-md h-8 px-3 py-0">
              <option :value="10">10</option>
              <option :value="20">20</option>
              <option :value="50">50</option>
            </select>
          </div>

          <span class="text-sm font-normal text-gray-500 dark:text-gray-400">
            Zobrazuji <span class="font-semibold text-gray-900 dark:text-white">{{
              (currentPage - 1) * limit + 1
            }}-{{ Math.min(currentPage * limit, totalCount) }}</span>
            z <span class="font-semibold text-gray-900 dark:text-white">{{ totalCount }}</span>
          </span>

          <ul class="inline-flex -space-x-px text-sm h-8">
            <li>
              <button @click="previousPage" :disabled="currentPage === 1"
                      class="flex items-center justify-center px-3 h-8 leading-tight text-gray-500 bg-white border border-gray-300 rounded-s-lg hover:bg-gray-100 disabled:opacity-50">
                Předchozí
              </button>
            </li>
            <template v-for="page in totalPages" :key="page">
              <li v-if="Math.abs(currentPage - page) <= 2 || page === 1 || page === totalPages">
                <button @click="goToPage(page)"
                        :class="{'text-blue-600 bg-blue-50': currentPage === page, 'text-gray-500 bg-white': currentPage !== page}"
                        class="flex items-center justify-center px-3 h-8 border border-gray-300 hover:bg-gray-100">
                  {{ page }}
                </button>
              </li>
            </template>
            <li>
              <button @click="nextPage" :disabled="currentPage === totalPages"
                      class="flex items-center justify-center px-3 h-8 leading-tight text-gray-500 bg-white border border-gray-300 rounded-e-lg hover:bg-gray-100 disabled:opacity-50">
                Další
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </default>
  </div>
</template>

<script lang="ts">
import Default from "@/layouts/admin/default.vue";
import {useAppStore} from "@/stores/appStore";
import {ResponseEntityAddress} from "@haxo-cz/orders-ts-client";
import {debounce} from "lodash";
import FlowbiteModal from "@/components/flowbiteDynamic/flowbiteModal.vue";
import CreateAddressForm from "@/components/forms/createAddressForm.vue";

export default {
  name: "AddressesView",
  components: {CreateAddressForm, FlowbiteModal, Default},
  data() {
    return {
      appStore: useAppStore(),
      addresses: [] as ResponseEntityAddress[],
      currentPage: 1,
      totalPages: 1,
      limit: 10,
      search: "",
      sortBy: "createdAt",
      sortOrder: "desc" as 'asc' | 'desc',
      isLoading: false,
      totalCount: 0,
    };
  },
  methods: {
    debouncedFetch() {
      this.currentPage = 1; // Resetujeme na první stranu při novém hledání
      this.fetchAddresses();
    },
    async fetchAddresses() {
      this.isLoading = true;
      try {
        // Použití $addressesApi podle zadání
        const response = await this.$addressesApi.findAllAddresses(
            this.appStore.selectedProject.spaceId,
            this.appStore.selectedProject._id,
            this.currentPage,
            this.limit,
            this.search.trim() || undefined,
            this.sortBy,
            this.sortOrder
        );

        // Mapování dat z PaginatedResultAddressResponse
        this.addresses = response.data.data;
        this.totalPages = response.data.meta.pagination.pages;
        this.totalCount = response.data.meta.totalCount;
      } catch (error) {
        console.error("Chyba při načítání adres:", error);
      } finally {
        this.isLoading = false;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.fetchAddresses();
      }
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.fetchAddresses();
      }
    },
    goToPage(page: number) {
      if (page !== this.currentPage) {
        this.currentPage = page;
        this.fetchAddresses();
      }
    },
  },
  created() {
    this.debouncedFetch = debounce(this.debouncedFetch, 500);
  },
  mounted() {
    this.fetchAddresses();
  },
  beforeUnmount() {
    if (this.debouncedFetch && 'cancel' in this.debouncedFetch) {
      (this.debouncedFetch as any).cancel();
    }
  }
};
</script>

<style scoped>
.loader {
  border-top-color: #3498db;
  animation: spinner 1.5s linear infinite;
}

@keyframes spinner {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>